import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/ui/Logo';
import {
  House,
  Users,
  Package,
  ClipboardText,
  Wrench,
  Receipt,
  Bank,
  CreditCard,
  ChartBar,
  CaretDown,
  CaretRight
} from '@phosphor-icons/react';

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

interface NavigationItem {
  name: string;
  href?: string;
  icon: any;
  children?: NavigationItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const { businessId } = useAuth();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set(['Repair Orders']));

  const navigation: NavigationItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: House },
    { name: 'Contacts', href: '/contacts', icon: Users },
    { name: 'Inventory', href: '/inventory', icon: Package },
    { name: 'Purchase Orders', href: '/purchase-orders', icon: ClipboardText },
    {
      name: 'Repair Orders',
      icon: Wrench,
      children: [
        { name: 'Repair Orders', href: '/repair-orders', icon: Wrench },
        { name: 'Repair Invoices', href: '/repair-invoices', icon: Receipt },
      ]
    },
    { name: 'Invoices', href: '/invoices', icon: Receipt },
    { name: 'Accounts', href: '/accounts', icon: Bank },
    { name: 'Payments', href: '/payments', icon: CreditCard },
    { name: 'Reports', href: '/reports', icon: ChartBar },
  ];

  const toggleExpanded = (itemName: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemName)) {
      newExpanded.delete(itemName);
    } else {
      newExpanded.add(itemName);
    }
    setExpandedItems(newExpanded);
  };

  const renderNavigationItem = (item: NavigationItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.name);
    const isActive = item.href && location.pathname === item.href;
    const hasActiveChild = hasChildren && item.children!.some(child => child.href && location.pathname === child.href);

    if (hasChildren) {
      return (
        <div key={item.name}>
          <button
            onClick={() => toggleExpanded(item.name)}
            className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
              hasActiveChild
                ? 'bg-primary-100 text-primary-700 shadow-md'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={{ paddingLeft: `${12 + level * 16}px` }}
          >
            <item.icon className="w-5 h-5 mr-3" />
            <span className="flex-1 text-left">{item.name}</span>
            {isExpanded ? (
              <CaretDown className="w-4 h-4 ml-2" />
            ) : (
              <CaretRight className="w-4 h-4 ml-2" />
            )}
          </button>
          {isExpanded && (
            <div className="ml-4">
              {item.children!.map(child => renderNavigationItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    if (!item.href) return null;

    return (
      <Link
        key={item.name}
        to={item.href}
        className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
          isActive
            ? 'bg-primary-100 text-primary-700 shadow-md'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
        style={{ paddingLeft: `${12 + level * 16}px` }}
        onClick={() => setSidebarOpen(false)}
      >
        <item.icon className="w-5 h-5 mr-3" />
        {item.name}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-start h-16 px-4">
            <Logo size="sm" showText={true} />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-2">
            {navigation.map((item) => renderNavigationItem(item))}
          </nav>

          {/* Footer */}
          <div className="px-4 py-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              RepairData v1.0
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;